import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations, CanModify, ExclusionTypes } from './configuration';
import { Typography } from '@material-ui/core';
import {
  AppDatePicker,
  AppRadiogroup,
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Roles, SchemeTypes } from '../../../../config';
import { FormFields } from '../../configuration';
import * as RegistrationDetailSelectors from 'redux/reducers/registration_detail_reducer/selectors';
import * as AuthSelectors from 'redux/reducers/gl_auth_reducer/selectors';

interface IProps {
  mode: 'VIEW' | 'EDIT';
  scheme_type: SchemeTypes;
  is_agent: boolean;
  isExcluded: boolean;
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
  exclusion_reason: string | null;
  hasIntermediary: boolean;
}

export default function ExclusionDetailsForm(props: IProps): ReactElement {
  // console.log('Render ExclusionDetailsForm');
  const mode = props.mode;
  const SCHEME_TYPE = props.scheme_type;
  const IS_AGENT = props.is_agent;
  const errors = props.errors;
  const control = props.control;
  const isExcluded = props.isExcluded;
  const classes = useStyles();
  const ROLE = AuthSelectors.useSelectUserRole();
  const hasIntermediary = props.hasIntermediary;
  let formElements = [
    {
      element: renderReasonField(
        errors,
        control,
        mode,
        SCHEME_TYPE,
        IS_AGENT,
        ROLE,
        hasIntermediary
      ),
      size: 12,
      id: 'reason',
    },
  ];

  if (props.exclusion_reason === '3' || props.exclusion_reason === '6')
    formElements.push({
      element: renderCessationDateField(
        errors,
        control,
        mode,
        SCHEME_TYPE,
        IS_AGENT,
        props.exclusion_reason,
        classes
      ),
      size: 4,
      id: 'cessation_date',
    });

  if (
    (SCHEME_TYPE === SchemeTypes.NON_EU && props.exclusion_reason === '3') ||
    props.exclusion_reason === '6'
  )
    formElements.push({
      element: (
        <CountryField
          errors={errors}
          control={control}
          mode={mode}
          SCHEME_TYPE={SCHEME_TYPE}
          IS_AGENT={IS_AGENT}
          reason={props.exclusion_reason}
        />
      ), // renderCountryField(errors, control, mode, SCHEME_TYPE, IS_AGENT),
      size: 4,
      id: 'country',
    });

  if (
    (SCHEME_TYPE === SchemeTypes.NON_EU && props.exclusion_reason === '3') ||
    props.exclusion_reason === '6'
  )
    formElements.push({
      element: renderVatNumberField(
        errors,
        control,
        mode,
        SCHEME_TYPE,
        IS_AGENT,
        props.exclusion_reason,
        classes
      ),
      size: 4,
      id: 'vat',
    });

  return (
    <div>
      <Typography variant="h5">EXCLUSION DETAILS</Typography>
      <GridLayout elements={formElements as any} justify="flex-start" />
    </div>
  );
}

const renderReasonField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  ROLE: Roles,
  hasIntermediary: boolean
) => {
  function getReasonsByRole() {
    if (ROLE === Roles.Admin) {
      if (
        SCHEME_TYPE == SchemeTypes.NON_EU ||
        (SCHEME_TYPE == SchemeTypes.IOSS && hasIntermediary)
      ) {
        return [
          ExclusionTypes[0],
          ExclusionTypes[1],
          ExclusionTypes[2],
          ExclusionTypes[3],
          ExclusionTypes[4],
        ];
      } else {
        return ExclusionTypes;
      }
    }
    return [
      ExclusionTypes[0],
      ExclusionTypes[2],
      ExclusionTypes[4],
      ExclusionTypes[5],
    ];
  }

  return (
    <AppRadiogroup
      id="exclusionDetails_reason"
      label=""
      error={'exclusionDetails_reason' in errors}
      name="exclusionDetails_reason"
      control={control}
      rules={{ required: 'Required Field' }}
      // rules={{}}
      helperText={
        errors.exclusionDetails_reason && errors.exclusionDetails_reason.message
      }
      values={getReasonsByRole()}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes('exclusionDetails_reason') ||
        mode == 'VIEW'
      }
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  reason: string,
  classes: any
) => {
  return (
    <React.Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="exclusionDetails_vatNum"
        label={<TranslatedText textMap={Translations.vat_number_label} />}
        error={'exclusionDetails_vatNum' in errors}
        name="exclusionDetails_vatNum"
        control={control}
        rules={{ required: 'Required Field' }}
        // rules={{}}
        helperText={
          errors.exclusionDetails_vatNum &&
          errors.exclusionDetails_vatNum.message
        }
        disabled={
          !CanModify[SCHEME_TYPE](IS_AGENT).includes(
            'exclusionDetails_vatNum'
          ) || mode == 'VIEW'
        }
      />
      <div className={classes.note}>
        <span>
          Please provide your VAT number issues by the other member state
        </span>
      </div>
    </React.Fragment>
  );
};

interface ICountryFieldProps {
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
  mode: 'VIEW' | 'EDIT';
  SCHEME_TYPE: SchemeTypes;
  IS_AGENT: boolean;
  reason: string;
}
const CountryField = ({
  errors,
  control,
  mode,
  SCHEME_TYPE,
  IS_AGENT,
  reason,
}: ICountryFieldProps) => {
  const classes = useStyles();
  // let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .nonMemberStateCountries.data;
  let countries = RegistrationDetailSelectors.useGetCountriesMemberState();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.country_label.English,
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: country.description,
      });
  return (
    <React.Fragment>
      <AppSelect
        variant="outlined"
        margin="normal"
        fullWidth
        label={<TranslatedText textMap={Translations.country_label} />}
        id="exclusionDetails_country"
        name="exclusionDetails_country"
        error={'exclusionDetails_country' in errors}
        control={control}
        rules={{ required: 'Required Field' }}
        // rules={{}}
        options={select_countries as any}
        helperText={
          errors.exclusionDetails_country &&
          errors.exclusionDetails_country.message
        }
        disabled={
          !CanModify[SCHEME_TYPE](IS_AGENT).includes(
            'exclusionDetails_country'
          ) || mode == 'VIEW'
        }
      />
      <div className={classes.note}>
        <span>Please select the member state</span>
      </div>
    </React.Fragment>
  );
};

const renderCessationDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  reason: string,
  classes: any
) => {
  return (
    <React.Fragment>
      <AppDatePicker
        autoOk
        fullWidth
        margin="normal"
        label="Cessation Date"
        error={'exclusionDetails_cessationDate' in errors}
        control={control}
        rules={{ required: 'Required Field' }}
        // rules={{}}
        name="exclusionDetails_cessationDate"
        helperText={
          errors.exclusionDetails_cessationDate &&
          errors.exclusionDetails_cessationDate.message
        }
        disabled={
          !CanModify[SCHEME_TYPE](IS_AGENT).includes(
            'exclusionDetails_cessationDate'
          ) || mode == 'VIEW'
        }
      />
      <div className={classes.note}>
        {reason === '6' && (
          <span>When did you cease to be identified in this country</span>
        )}
        {/*Non union or import not intermediary*/}
        {reason === '3' &&
          (SCHEME_TYPE === SchemeTypes.NON_EU ||
            (SCHEME_TYPE === SchemeTypes.IOSS && !IS_AGENT)) && (
            <span>
              When did you start having a Fixed Establishement in the European
              Union
            </span>
          )}
        {/* eu scheme, interdmidiary */}
        {reason === '3' &&
          (SCHEME_TYPE === SchemeTypes.EU ||
            (SCHEME_TYPE === SchemeTypes.IOSS && IS_AGENT)) && (
            <span>
              When did you cease having a Fixed Establishement in the European
              Union
            </span>
          )}
      </div>
    </React.Fragment>
  );
};

const renderCommentsField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="exclusionDetails_comments"
      label={<TranslatedText textMap={Translations.comments_label} />}
      error={'exclusionDetails_comments' in errors}
      name="exclusionDetails_comments"
      control={control}
      rules={{ required: 'Required Field' }}
      // rules={{}}
      helperText={
        errors.exclusionDetails_comments &&
        errors.exclusionDetails_comments.message
      }
      multiline
      rows={4}
      rowsMax={6}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'exclusionDetails_comments'
        ) || mode === 'VIEW'
      }
    />
  );
};
